/**
 * applyAnulacionVenta.ts — Anulación total/parcial persistida en localStorage.
 *
 * QUÉ HACE: Valida, aplica y persiste anulaciones (venta, cierres, stock, movimientos).
 * QUÉ EXPORTA: executeAnulacionVenta, validateAnulacionItems, aggregateAnulacionesByProducto.
 * QUÉ NO DEBE HACER: Importar React ni llamar APIs directamente.
 */

import { defaultProducts } from "@/mocks/data/products";
import type {
  AnulacionItem,
  AnulacionPayload,
  AnulacionVenta,
  CierreCaja,
  MovimientoCaja,
  Venta,
} from "../types/caja.types";
import {
  getSelectedPuntoCajaIdFromLs,
  nombrePuntoFromCatalogLocal,
  readSesionActivaForPunto,
} from "./cajaSessionLocal";
import {
  runTransaction,
  txRead,
  txReadRaw,
  txWrite,
} from "@/lib/persistence/transactionalStore";

const VENTAS_KEY = "caja_ventas_historial";
const CIERRES_KEY = "caja_cierres_historial";
const MOVIMIENTOS_KEY = "caja_movimientos";
const PRODUCTS_KEY = "inventory_products";
const STOCK_MOV_KEY = "inventory_stock_movements";

// Participan en la transacción activa (anulación atómica: ventas + cierres +
// stock + movimientos + auditoría como una sola unidad todo-o-nada).
const get = <T>(key: string, def: T): T => txRead(key, def);
const set = <T>(key: string, val: T): void => txWrite(key, val);

const readProducts = (): Array<Record<string, unknown>> => {
  if (txReadRaw(PRODUCTS_KEY) === null) {
    try {
      return structuredClone(defaultProducts) as unknown as Array<
        Record<string, unknown>
      >;
    } catch {
      return JSON.parse(JSON.stringify(defaultProducts)) as unknown as Array<
        Record<string, unknown>
      >;
    }
  }
  return get(PRODUCTS_KEY, []) as Array<Record<string, unknown>>;
};

export interface AnulacionExecuteMeta {
  anuladaPor: string;
  usuarioId?: string;
}

export function sumAnulacionesMonto(venta: Venta): number {
  return (venta.anulaciones ?? []).reduce(
    (acc, a) => acc + (a.montoAnulado ?? 0),
    0,
  );
}

export interface AnulacionItemAgregado {
  productoId: string;
  nombre: string;
  cantidad: number;
  monto: number;
}

/** Totales por producto de todo el historial de anulaciones (solo lectura). */
export function aggregateAnulacionesByProducto(
  anulaciones: AnulacionVenta[],
): AnulacionItemAgregado[] {
  const map = new Map<string, AnulacionItemAgregado>();
  for (const anu of anulaciones) {
    for (const item of anu.items) {
      const key = item.productoId;
      const prev = map.get(key);
      const montoLinea =
        item.subtotalLinea ?? item.cantidad * (item.precioUnitario ?? 0);
      if (prev) {
        prev.cantidad += item.cantidad;
        prev.monto += montoLinea;
      } else {
        map.set(key, {
          productoId: item.productoId,
          nombre: item.nombre,
          cantidad: item.cantidad,
          monto: montoLinea,
        });
      }
    }
  }
  return [...map.values()];
}

function cantidadDisponiblePorProducto(
  productos: Venta["productos"],
): Map<string, { nombre: string; cantidad: number }> {
  const map = new Map<string, { nombre: string; cantidad: number }>();
  for (const p of productos) {
    const prev = map.get(p.productoId);
    if (prev) {
      prev.cantidad += p.cantidad;
    } else {
      map.set(p.productoId, { nombre: p.nombre, cantidad: p.cantidad });
    }
  }
  return map;
}

/** Impide anular más unidades de las que siguen activas en la venta. */
export function validateAnulacionItems(
  productos: Venta["productos"],
  items: AnulacionPayload["items"],
): void {
  if (items.length === 0) {
    throw new Error("Debes seleccionar al menos un ítem para anular");
  }
  const disponible = cantidadDisponiblePorProducto(productos);
  const solicitado = new Map<string, number>();

  for (const item of items) {
    if (item.cantidad <= 0) {
      throw new Error(`Cantidad inválida para ${item.nombre}`);
    }
    solicitado.set(
      item.productoId,
      (solicitado.get(item.productoId) ?? 0) + item.cantidad,
    );
  }

  for (const [productoId, qty] of solicitado) {
    const disp = disponible.get(productoId);
    if (!disp || disp.cantidad <= 0) {
      const nombre = items.find((i) => i.productoId === productoId)?.nombre;
      throw new Error(
        `«${nombre ?? productoId}» ya fue anulado y no tiene unidades activas`,
      );
    }
    if (qty > disp.cantidad) {
      throw new Error(
        `«${disp.nombre}»: solo quedan ${disp.cantidad} unidad(es) activa(s). No puedes anular ${qty}.`,
      );
    }
  }
}

/** Aplica la anulación sobre una venta en memoria (sin side-effects). */
export function applyAnulacionToVenta(
  venta: Venta,
  payload: AnulacionPayload,
  meta: AnulacionExecuteMeta,
): { venta: Venta; esAnulacionTotal: boolean; montoAnulado: number } {
  if (venta.estado === "anulada") {
    throw new Error("La venta ya está anulada por completo");
  }

  const productosVenta = venta.productos ?? [];
  if (productosVenta.length === 0 || Number(venta.total || 0) <= 0) {
    throw new Error("No quedan ítems activos para anular en esta venta");
  }

  const itemsBody = payload.items ?? [];
  const itemsParaStock: AnulacionItem[] =
    itemsBody.length > 0
      ? itemsBody
      : productosVenta.map((p) => ({
          productoId: p.productoId,
          nombre: p.nombre,
          cantidad: p.cantidad,
          peso: p.peso,
          precioUnitario: p.precioUnitario,
        }));

  validateAnulacionItems(productosVenta, itemsParaStock);

  const montoCalculado = itemsParaStock.reduce(
    (acc, i) => acc + (i.subtotalLinea ?? i.cantidad * (i.precioUnitario ?? 0)),
    0,
  );
  const saldoVenta = Number(venta.total || 0);
  const montoAnulado = Math.min(
    saldoVenta,
    typeof payload.montoAnulado === "number" && payload.montoAnulado > 0
      ? payload.montoAnulado
      : montoCalculado,
  );

  if (montoAnulado <= 0) {
    throw new Error("El monto a anular debe ser mayor a cero");
  }

  const solicitadoPorProducto = new Map<string, number>();
  for (const item of itemsParaStock) {
    solicitadoPorProducto.set(
      item.productoId,
      (solicitadoPorProducto.get(item.productoId) ?? 0) + item.cantidad,
    );
  }

  const nextProductos = productosVenta
    .map((p) => {
      const anulQty = solicitadoPorProducto.get(p.productoId) ?? 0;
      if (anulQty <= 0) return p;
      const qtyNueva = p.cantidad - anulQty;
      if (qtyNueva <= 0) return null;
      return { ...p, cantidad: qtyNueva };
    })
    .filter((p): p is Venta["productos"][number] => p != null);

  const quedaActivo =
    nextProductos.length > 0 && Math.max(0, saldoVenta - montoAnulado) > 0;

  const esAnulacionTotal = !quedaActivo;

  const nuevaAnulacion: AnulacionVenta = {
    id: `ANU-${Date.now()}`,
    ventaId: venta.id,
    fecha: new Date().toISOString(),
    modo: esAnulacionTotal ? "total" : "parcial",
    items: itemsParaStock.map((i) => ({
      productoId: i.productoId,
      nombre: i.nombre,
      cantidad: i.cantidad,
      precioUnitario: i.precioUnitario ?? 0,
      precioCatalogo: i.precioCatalogo,
      subtotalLinea: i.subtotalLinea,
    })),
    montoAnulado,
    motivoAnulacion: payload.motivoAnulacion || "Sin motivo",
    usuarioId: meta.usuarioId || "unknown",
    userName: meta.anuladaPor || "Desconocido",
  };

  const next: Venta = {
    ...venta,
    anulaciones: [...(venta.anulaciones ?? []), nuevaAnulacion],
    productos: nextProductos,
    total: Math.max(0, saldoVenta - montoAnulado),
    estado: esAnulacionTotal ? "anulada" : "anulacion-parcial",
  };

  if (typeof venta.subtotal === "number") {
    next.subtotal = Math.max(0, venta.subtotal - montoAnulado);
  }

  if (esAnulacionTotal) {
    next.anuladaEn = new Date().toISOString();
    next.anuladaPor = meta.anuladaPor;
    next.motivoAnulacion = payload.motivoAnulacion || "Sin motivo";
    next.productos = [];
    next.total = 0;
    if (typeof venta.subtotal === "number") next.subtotal = 0;
  }

  return { venta: next, esAnulacionTotal, montoAnulado };
}

function patchVentaInCierreSnapshots(ventaId: string, updated: Venta): void {
  const cierres = get(CIERRES_KEY, []) as CierreCaja[];
  let changed = false;
  const nextCierres = cierres.map((cierre) => {
    if (!cierre.ventasSnapshot?.some((v) => v.id === ventaId)) return cierre;
    changed = true;
    return {
      ...cierre,
      ventasSnapshot: cierre.ventasSnapshot.map((v) =>
        v.id === ventaId ? { ...v, ...updated } : v,
      ),
    };
  });
  if (changed) set(CIERRES_KEY, nextCierres);
}

function restoreStockForItems(
  items: AnulacionPayload["items"],
  ventaId: string,
  motivo: string,
  userName: string,
  esTotal: boolean,
): void {
  const products = readProducts();
  const updated = products.map((p) => {
    const itemsAnulados = items.filter((i) => i.productoId === p.id);
    if (itemsAnulados.length === 0) return p;
    const totalARestaurar = itemsAnulados.reduce((acc, item) => {
      return acc + (p.ventaPorPeso ? item.peso || 0 : item.cantidad || 0);
    }, 0);
    return { ...p, stock: (Number(p.stock) || 0) + totalARestaurar };
  });
  set(PRODUCTS_KEY, updated);

  const stockMovs = get(STOCK_MOV_KEY, []) as Array<Record<string, unknown>>;
  const label = esTotal ? "Anulación" : "Anulación parcial";
  for (const item of items) {
    const dbProduct = products.find((p) => p.id === item.productoId);
    if (!dbProduct) continue;
    const cantidad = dbProduct.ventaPorPeso
      ? item.peso || 0
      : item.cantidad || 0;
    if (cantidad <= 0) continue;
    const stockAntes = Number(dbProduct.stock);
    stockMovs.unshift({
      id: `mov_anu_${Date.now()}_${item.productoId}`,
      productoId: dbProduct.id,
      tipo: "anulacion",
      cantidad,
      stockAntes,
      stockDespues: stockAntes + cantidad,
      nota: `${label} de venta ${ventaId} — ${motivo}`,
      usuarioNombre: userName,
      ventaId,
      createdAt: new Date().toISOString(),
    });
  }
  set(STOCK_MOV_KEY, stockMovs);
}

function registerMovimientoCaja(
  venta: Venta,
  montoAnulado: number,
  motivo: string,
  meta: AnulacionExecuteMeta,
  esTotal: boolean,
): void {
  if (montoAnulado <= 0) return;
  const puntoId =
    venta.puntoCajaId || getSelectedPuntoCajaIdFromLs() || "punto-default";
  const sesion =
    readSesionActivaForPunto(puntoId) ??
    (venta.sesionId ? { id: venta.sesionId } : null);
  const sesionId = venta.sesionId || (sesion?.id as string | undefined);
  if (!sesionId) return;

  const movimientos = get(MOVIMIENTOS_KEY, []) as MovimientoCaja[];
  const etiqueta = esTotal ? "Anulación" : "Anulación parcial";
  movimientos.unshift({
    id: `MOV-ANU-${Date.now()}`,
    tipo: "anulacion",
    monto: montoAnulado,
    motivo: `${etiqueta} de venta ${venta.id}: ${motivo}`,
    fecha: new Date().toISOString(),
    usuarioId: meta.usuarioId || "unknown",
    userName: meta.anuladaPor || "Desconocido",
    metodoPago: venta.metodoPago as MovimientoCaja["metodoPago"],
    sesionId,
    puntoCajaId: puntoId,
    puntoCajaNombre:
      venta.puntoCajaNombre ||
      nombrePuntoFromCatalogLocal(puntoId) ||
      undefined,
    ventaId: venta.id,
    estado: "activo",
  });
  set(MOVIMIENTOS_KEY, movimientos);
}

/** Persiste anulación en ventas, cierres, stock y movimientos de caja. */
export function executeAnulacionVenta(
  ventaId: string,
  payload: AnulacionPayload,
  meta: AnulacionExecuteMeta,
): Venta {
  // Todo el efecto de la anulación es una única transacción atómica y durable.
  const updated = runTransaction((): Venta => {
    const ventas = get(VENTAS_KEY, []) as Venta[];
    const idx = ventas.findIndex((v) => v.id === ventaId);
    if (idx === -1) {
      throw new Error("Venta no encontrada");
    }

    const {
      venta: ventaActualizada,
      esAnulacionTotal,
      montoAnulado,
    } = applyAnulacionToVenta(ventas[idx], payload, meta);

    const nextVentas = [...ventas];
    nextVentas[idx] = ventaActualizada;
    set(VENTAS_KEY, nextVentas);
    patchVentaInCierreSnapshots(ventaId, ventaActualizada);

    const itemsStock =
      payload.items.length > 0
        ? payload.items
        : (ventas[idx].productos ?? []).map((p) => ({
            productoId: p.productoId,
            nombre: p.nombre,
            cantidad: p.cantidad,
            peso: p.peso,
            precioUnitario: p.precioUnitario,
          }));

    restoreStockForItems(
      itemsStock,
      ventaId,
      payload.motivoAnulacion || "Sin motivo",
      meta.anuladaPor,
      esAnulacionTotal,
    );
    registerMovimientoCaja(
      ventaActualizada,
      montoAnulado,
      payload.motivoAnulacion || "Sin motivo",
      meta,
      esAnulacionTotal,
    );

    return ventaActualizada;
  });

  // Señales de refresco de UI: solo tras el commit durable (datos ya persistidos).
  try {
    window.dispatchEvent(new CustomEvent("inventory-stock-changed"));
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent("caja-movimientos-updated"));
  } catch {
    /* ignore */
  }

  return updated;
}

export function processLocalAnulacion(
  ventaId: string,
  payload: AnulacionPayload & AnulacionExecuteMeta,
): Venta {
  return executeAnulacionVenta(ventaId, payload, {
    anuladaPor: payload.anuladaPor,
    usuarioId: payload.usuarioId,
  });
}

/** Conserva anulaciones parciales al re-sincronizar ventas demo desde cierres. */
export function mergeVentaPreservingAnulacion(
  fresh: Venta,
  existing?: Venta,
): Venta {
  if (!existing) return fresh;
  const tieneAnulacion =
    (existing.anulaciones?.length ?? 0) > 0 ||
    existing.estado === "anulacion-parcial" ||
    existing.estado === "anulada";
  if (!tieneAnulacion) return fresh;
  return {
    ...fresh,
    estado: existing.estado,
    total: existing.total,
    subtotal: existing.subtotal,
    productos: existing.productos,
    anulaciones: existing.anulaciones,
    anuladaEn: existing.anuladaEn,
    anuladaPor: existing.anuladaPor,
    motivoAnulacion: existing.motivoAnulacion,
  };
}
